import {
  createStyles,
  IconButton,
  makeStyles,
  Menu,
  MenuItem as MuiMenuItem,
  styled,
  Theme,
} from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import React, { MouseEvent, useState } from 'react';
import { Dialog } from '../common/Dialog';
import { Divider } from '../common/Divider';
import { Typography } from '../common/Typography';
import { DeleteModal, DeleteModalProps } from './DeleteModal';
import { MoveContentModal, MoveContentModalProps } from './MoveContentModal';
import { RenameModal, RenameModalProps } from './RenameModal';
import { ShareUploadModal, ShareUploadModalProps } from './ShareUploadModal';
import { useUploadContext } from './UploadManagementContext';

export interface ContentActionsProps {
  iconVertical?: boolean;
  renameConfig?: Omit<RenameModalProps, 'onClose'>;
  moveConfig?: Omit<MoveContentModalProps, 'onClose'>;
  deleteConfig?: Omit<DeleteModalProps, 'onClose'>;
  shareConfig?: Omit<ShareUploadModalProps, 'onClose'>;
  openViewConfig?: {
    label: string;
    onClick: () => void;
  };
}

interface ContentActionsState {
  anchorEl: HTMLElement | null;
  openedModal: 'share' | 'rename' | 'move' | 'delete' | 'none';
}

const initialState: ContentActionsState = {
  anchorEl: null,
  openedModal: 'none',
};

export const ContentActions: React.FunctionComponent<ContentActionsProps> = ({
  deleteConfig,
  renameConfig,
  moveConfig,
  shareConfig,
  openViewConfig,
  iconVertical,
}) => {
  const { view } = useUploadContext();
  const [{ anchorEl, openedModal }, setState] = useState(initialState);
  const classes = useStyles();
  const handleOnClick = (event: MouseEvent<HTMLElement>) => {
    setState((state) => ({ ...state, anchorEl: event.currentTarget }));
  };

  const handleOnClose = () => setState({ openedModal: 'none', anchorEl: null });
  const openShare = () => setState({ openedModal: 'share', anchorEl: null });
  const openRename = () => setState({ openedModal: 'rename', anchorEl: null });
  const openMove = () => setState({ openedModal: 'move', anchorEl: null });
  const openRemove = () => setState({ openedModal: 'delete', anchorEl: null });
  const openView = () => {
    setState({ openedModal: 'none', anchorEl: null });
    openViewConfig?.onClick();
  };

  if (view === 'shared') {
    return null;
  }

  return (
    <div>
      <IconButton
        onClick={handleOnClick}
        aria-haspopup={Boolean(anchorEl)}
        aria-label={`open action menu`}
        size={'small'}
      >
        {iconVertical ? (
          <MoreVert className={classes.actionsIcon} />
        ) : (
          <MoreHorizIcon className={classes.actionsIcon} />
        )}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleOnClose}
        aria-labelledby={`open action menu`}
        PaperProps={{ className: classes.popOver }}
      >
        {shareConfig && (
          <MenuItem onClick={openShare}>
            <Typography>Share Options</Typography>
          </MenuItem>
        )}

        {openViewConfig && (
          <MenuItem onClick={openView}>
            <Typography>{openViewConfig.label}</Typography>
          </MenuItem>
        )}

        {renameConfig && (
          <MenuItem onClick={openRename}>
            <Typography>Rename</Typography>
          </MenuItem>
        )}

        {moveConfig && (
          <MenuItem onClick={openMove}>
            <Typography>Move</Typography>
          </MenuItem>
        )}
        <Divider />

        {deleteConfig && (
          <MenuItem onClick={openRemove}>
            <Typography>Delete</Typography>
          </MenuItem>
        )}
      </Menu>

      {shareConfig && (
        <Dialog aria-label='share' open={openedModal === 'share'}>
          <ShareUploadModal onClose={handleOnClose} {...shareConfig} />
        </Dialog>
      )}

      {renameConfig && (
        <Dialog aria-label='rename' open={openedModal === 'rename'}>
          <RenameModal onClose={handleOnClose} {...renameConfig} />
        </Dialog>
      )}

      {moveConfig && (
        <Dialog aria-label='move' open={openedModal === 'move'}>
          <MoveContentModal onClose={handleOnClose} {...moveConfig} />
        </Dialog>
      )}

      {deleteConfig && (
        <Dialog aria-label='delete' open={openedModal === 'delete'}>
          <DeleteModal onClose={handleOnClose} {...deleteConfig} />
        </Dialog>
      )}
    </div>
  );
};

export const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100],
  },
}));

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionsIcon: {
      fontSize: 28,
    },
    popOver: {
      borderRadius: 0,
      border: `solid ${theme.palette.grey[500]} 1px`,
    },
  }),
);
